import {
  CopyIcon, KeyboardIcon, LinkIcon, MoonIcon, RowsIcon, ShieldCheckIcon, SignOutIcon, SunIcon, TreeStructureIcon,
  CircleHalfIcon, MagnifyingGlassIcon,
} from '@phosphor-icons/react'
import { parseId, parseSkillKey, toast, useUI, type PaletteCommand } from '@robotensor/ui'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo } from 'react'
import type { NavDef } from './nav.tsx'
import { useSite } from './site.tsx'

interface PaletteDeps {
  nav: readonly NavDef[]
  perms: ReadonlySet<string>
  userId: string
  onShortcuts: () => void
  onSignOut: () => void
  /** Offer theme commands (off on a single-theme site such as the contributor app). */
  themes?: boolean
}

export function usePalette({ nav, perms, userId, onShortcuts, onSignOut, themes = true }: PaletteDeps) {
  const navigate = useNavigate()
  const ui = useUI()
  const site = useSite()

  const commands = useMemo<PaletteCommand[]>(() => {
    const go: PaletteCommand[] = nav.map((n) => ({
      id: `nav:${n.id}`,
      title: n.label,
      group: 'Go to',
      keywords: [...(n.keywords ?? []), n.description ?? ''],
      icon: <n.icon />,
      keys: n.keys,
      hint: n.to,
      run: () => void navigate({ to: n.to }),
    }))
    const actions: PaletteCommand[] = [
      ...(themes ? themeCommands(ui.setTheme) : []),
      {
        id: 'act:density',
        title: ui.density === 'compact' ? 'Comfortable density' : 'Compact density',
        group: 'Preferences',
        keywords: ['rows', 'spacing', 'table'],
        icon: <RowsIcon />,
        run: () => ui.setDensity(ui.density === 'compact' ? 'comfortable' : 'compact'),
      },
      { id: 'act:shortcuts', title: 'Keyboard shortcuts', group: 'Help', keywords: ['keys', 'help', 'hotkeys'], icon: <KeyboardIcon />, keys: '?', run: onShortcuts },
      ...(site.helpLinks ?? []).map((h) => ({ id: `help:${h.key}`, title: h.label, group: 'Help', keywords: ['help', ...(h.keywords ?? [])], icon: h.icon, hint: 'opens in a new tab', run: () => void window.open(h.href, '_blank', 'noopener,noreferrer') }) satisfies PaletteCommand),
      {
        id: 'act:copy-link',
        title: 'Copy link to this view',
        group: 'Actions',
        keywords: ['share', 'url', 'permalink'],
        icon: <LinkIcon />,
        run: () => {
          void navigator.clipboard.writeText(window.location.href).then(
            () => toast({ title: 'Link copied', description: 'It restores the filters, selection and playhead you see now.' }),
            () => toast({ title: 'Couldn’t copy', tone: 'warn' }),
          )
        },
      },
      { id: 'act:copy-id', title: 'Copy my user id', group: 'Actions', keywords: ['usr', 'account', 'id'], icon: <CopyIcon />, hint: userId, run: () => void navigator.clipboard.writeText(userId).then(() => toast({ title: 'Id copied', description: userId })) },
      ...site.accountLinks.map((a) => ({ id: `act:${a.key}`, title: a.label, group: 'Account', keywords: ['account', 'profile'], icon: a.icon, keys: a.keys, run: () => void navigate({ to: a.to }) }) satisfies PaletteCommand),
      ...(perms.has('audit:view') && site.id === 'ops'
        ? [{ id: 'act:verify', title: 'Verify audit chain', group: 'Actions', keywords: ['integrity', 'hash', 'tamper'], icon: <ShieldCheckIcon />, run: () => void navigate({ to: '/admin/audit' as never, search: { verify: '1' } as never }) } satisfies PaletteCommand]
        : []),
      { id: 'act:signout', title: 'Sign out', group: 'Account', keywords: ['logout', 'leave'], icon: <SignOutIcon />, run: onSignOut },
    ]
    return [...go, ...actions]
  }, [nav, navigate, ui, onShortcuts, onSignOut, perms, userId, site, themes])

  const resolve = useCallback(
    (query: string): PaletteCommand[] => {
      const q = query.trim()
      const id = parseId(q)
      if (id && id.complete) {
        if (!id.known) return [{ id: 'id:unknown', title: `Unknown id prefix “${id.prefix}_”`, group: 'Open id', icon: <MagnifyingGlassIcon />, run: () => {} }]
        const r = site.routeForId(id.value, perms, userId)
        if (!r) return [{ id: 'id:none', title: `No page for ${id.kind} ids yet`, group: 'Open id', hint: id.value, icon: <MagnifyingGlassIcon />, run: () => {} }]
        return [{ id: `id:${id.value}`, title: `${r.label}`, group: 'Open id', hint: id.value, icon: <MagnifyingGlassIcon />, run: () => void navigate({ to: r.to as never, search: r.search as never }) }]
      }
      if (id && !id.complete && id.known && id.body.length > 0) {
        return [{ id: 'id:partial', title: `Keep typing: ${/^[aeiou]/i.test(id.kind ?? '') ? 'an' : 'a'} ${id.kind} id has 26 characters after “${id.prefix}_”`, group: 'Open id', hint: `${id.body.length}/26`, icon: <MagnifyingGlassIcon />, run: () => {} }]
      }
      const sk = parseSkillKey(q)
      if (sk && nav.some((n) => n.id === 'catalog')) return [{ id: `skill:${sk.value}`, title: `Open ${sk.value} in the catalog`, group: 'Open id', icon: <TreeStructureIcon />, run: () => void navigate({ to: '/catalog' as never, search: { view: 'explorer', node: sk.value } as never }) }]
      return []
    },
    [perms, userId, navigate, site, nav],
  )

  /** After the ranked results: hand the query to the library's full-text search. */
  const fallback = useCallback(
    (query: string): PaletteCommand[] => {
      const q = query.trim()
      if (q.length < 2 || parseId(q) || !nav.some((n) => n.id === 'catalog')) return []
      return [{ id: `search:library`, title: `Search the catalog for “${q}”`, group: 'Search', icon: <MagnifyingGlassIcon />, run: () => void navigate({ to: '/catalog' as never, search: { view: 'table', q } as never }) }]
    },
    [nav, navigate],
  )

  return { commands, resolve, fallback }
}


function themeCommands(setTheme: (t: 'light' | 'dark' | 'system') => void): PaletteCommand[] {
  return [
    { id: 'act:theme-light', title: 'Use light theme', group: 'Preferences', keywords: ['appearance', 'drafting paper'], icon: <SunIcon />, run: () => setTheme('light') },
    { id: 'act:theme-dark', title: 'Use dark theme', group: 'Preferences', keywords: ['appearance', 'graphite', 'night'], icon: <MoonIcon />, run: () => setTheme('dark') },
    { id: 'act:theme-system', title: 'Match system theme', group: 'Preferences', keywords: ['appearance', 'auto'], icon: <CircleHalfIcon />, run: () => setTheme('system') },
  ]
}
